// src/Pages/Blog/BlogDetailPage.jsx
import React, { useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  Typography,
  Chip,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import PlaceholderImage from './PlaceholderImage';
import { BLOG_POSTS } from './blogData';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Find the blog post
  const post = BLOG_POSTS.find(p => p.slug === slug);
  // Old slug (renamed post)? Send the visitor to the current URL.
  const renamedTo = !post
    ? BLOG_POSTS.find(p => (p.redirectFrom || []).includes(slug))
    : null;

  // Get related posts (same category, exclude current) - ENHANCED DEDUPLICATION
  const relatedPosts = post
    ? BLOG_POSTS.filter(p => 
        p.id !== post.id && 
        p.slug !== post.slug &&  // Extra safety check
        p.categories.some(cat => post.categories.includes(cat))
      )
      // Remove duplicates by both ID and slug (comprehensive deduplication)
      .filter((p, index, self) => 
        index === self.findIndex(t => t.id === p.id || t.slug === p.slug)
      )
      // Limit to 3 posts
      .slice(0, 3)
    : [];



  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post && renamedTo) {
    return <Navigate to={`/blog/${renamedTo.slug}`} replace />;
  }

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Article Not Found
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          The article you're looking for doesn't exist or has been removed.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/blog')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = `https://www.revfer.com/blog/${slug}`;

  // Signup attribution: same UTM scheme as ads; campaign = post slug.
  // Signup attribution. Default: the blog itself is the source (organic visitors).
  // If the reader arrived with UTM tags (e.g. a LinkedIn share link), pass THOSE
  // through to /m instead, so the original channel is what gets recorded; the
  // post goes in utm_content. Kept in sessionStorage so it survives clicking
  // through to another post. /m and signup are unchanged.
  const landingUtm = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('utm_source')) {
        const keep = {};
        for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term']) {
          if (qs.get(k)) keep[k] = qs.get(k).slice(0, 200);
        }
        sessionStorage.setItem('revfer_blog_landing_utm', JSON.stringify(keep));
        return keep;
      }
      return JSON.parse(sessionStorage.getItem('revfer_blog_landing_utm') || 'null');
    } catch {
      return null;
    }
  })();
  const signupParams = new URLSearchParams(
    landingUtm?.utm_source
      ? { ...landingUtm, utm_content: post.slug }
      : { utm_source: 'blog', utm_medium: 'content', utm_campaign: post.slug }
  );
  const signupUrl = `/m?${signupParams.toString()}`;
  // In-post CTAs are written as href="/" in the post files; point them at the tracked signup URL.
  const contentHtml = post.content.replace(/href="\/"/g, `href="${signupUrl}"`);
  const shareTitle = encodeURIComponent(post.title);

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`
    };

    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  // Article Schema for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "image": post.image,
    "datePublished": post.publishedDate,
    "dateModified": post.modifiedDate,
    "author": {
      "@type": "Person",
      "name": post.author.name,
      "jobTitle": post.author.role,
      "image": post.author.avatar
    },
    "publisher": {
      "@type": "Organization",
      "name": "Revfer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.revfer.com/logo.png"
      }
    },
    "description": post.excerpt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": shareUrl
    },
    "keywords": post.tags.join(', '),
    "articleSection": post.categories.join(', '),
    "wordCount": post.content.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.revfer.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://www.revfer.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": shareUrl
      }
    ]
  };

  // FAQ Schema for SEO
  const faqSchema = post.faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  return (
    <>
      <EnhancedSEO 
        title={post.seo?.metaTitle || `${post.title} | Revfer Blog`}
        description={post.seo?.metaDescription || post.excerpt}
        keywords={post.seo?.keywords?.join(', ') || post.tags.join(', ')}
        type="article"
        image={post.seo?.ogImage || post.image}
        author={post.author.name}
        publishedTime={post.publishedDate}
        modifiedTime={post.modifiedDate}
        canonical={post.seo?.canonicalUrl}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title, url: `/blog/${slug}` }
        ]}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
        <meta name="article:published_time" content={post.publishedDate} />
        <meta name="article:modified_time" content={post.modifiedDate} />
        <meta name="article:author" content={post.author.name} />
        <meta name="article:section" content={post.categories[0]} />
        {(post.seo?.keywords || post.tags).map(tag => (
          <meta key={tag} name="article:tag" content={tag} />
        ))}
        {/* Twitter Card */}
        <meta name="twitter:card" content={post.seo?.twitterCard || "summary_large_image"} />
        <meta name="twitter:title" content={post.seo?.metaTitle || post.title} />
        <meta name="twitter:description" content={post.seo?.metaDescription || post.excerpt} />
        <meta name="twitter:image" content={post.seo?.ogImage || post.image} />
        {/* Focus Keyword for SEO tools */}
        {post.seo?.focusKeyword && (
          <meta name="focus-keyword" content={post.seo.focusKeyword} />
        )}
      </Helmet>

      {/* Back Button */}
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 3 }}
        >
          Back to Blog
        </Button>
      </Container>

      {/* Hero Image */}
      <Box
        sx={{
          width: '100%',
          height: { xs: 250, sm: 350, md: 500 },
          overflow: 'hidden',
          mb: 4
        }}
      >
        {post.image ? (
          <Box
            component="img"
            src={post.image}
            alt={post.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        ) : (
          <PlaceholderImage
            title={post.title}
            categories={post.categories}
            height={500}
          />
        )}
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        {/* Categories */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {post.categories.map(cat => (
            <Chip
              key={cat}
              label={cat}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>

        {/* Title */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 700,
            mb: 3,
            lineHeight: 1.2
          }}
        >
          {post.title}
        </Typography>

        {/* Excerpt */}
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            mb: 4,
            fontSize: { xs: '1rem', md: '1.2rem' },
            lineHeight: 1.6
          }}
        >
          {post.excerpt}
        </Typography>

        {/* Author Info & Meta */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 4,
            pb: 3,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={post.author.avatar}
              alt={post.author.name}
              sx={{ width: 56, height: 56 }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {post.author.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {post.author.role}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScheduleIcon fontSize="small" />
                  {formatDate(post.publishedDate)}
                </Typography>
                {post.modifiedDate && formatDate(post.modifiedDate) !== formatDate(post.publishedDate) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Updated {formatDate(post.modifiedDate)}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {post.readTime} min read
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Share Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<FacebookIcon />}
              onClick={() => handleShare('facebook')}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {!isMobile && 'Share'}
            </Button>
            <Button
              size="small"
              startIcon={<TwitterIcon />}
              onClick={() => handleShare('twitter')}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {!isMobile && 'Tweet'}
            </Button>
            <Button
              size="small"
              startIcon={<LinkedInIcon />}
              onClick={() => handleShare('linkedin')}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {!isMobile && 'Share'}
            </Button>
          </Box>
        </Box>

        {/* Article Content */}
        <Box
          sx={{
            '& p': {
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.8,
              mb: 3,
              color: 'text.primary'
            },
            '& h2': {
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 600,
              mt: 5,
              mb: 2,
              color: 'text.primary',
              clear: 'both' // Clear floated images before h2 headings
            },
            '& h3': {
              fontSize: { xs: '1.3rem', md: '1.5rem' },
              fontWeight: 600,
              mt: 4,
              mb: 2,
              color: 'text.primary'
            },
            '& h4': {
              fontSize: { xs: '1.2rem', md: '1.3rem' },
              fontWeight: 600,
              mt: 3,
              mb: 2,
              color: 'text.primary'
            },
            '& ul, & ol': {
              mb: 3,
              pl: 3
            },
            '& li': {
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.8,
              mb: 1
            },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 3,
              py: 1,
              my: 3,
              fontStyle: 'italic',
              fontSize: { xs: '1.1rem', md: '1.2rem' },
              color: 'text.secondary'
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              mb: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            },
            '& th, & td': {
              padding: '1rem',
              border: '1px solid #ddd',
              textAlign: 'left'
            },
            '& th': {
              background: 'linear-gradient(135deg, #0D2B6B 0%, #1565C0 100%)',
              color: 'white',
              fontWeight: 600
            },
            '& tr:nth-of-type(even)': {
              background: '#f9fafb'
            },
            // Floated image handling
            '& figure': {
              margin: 0,
              '@media (max-width: 600px)': {
                float: 'none !important',
                width: '100% !important',
                maxWidth: '100% !important',
                margin: '2rem 0 !important'
              }
            },
            '& figure img': {
              display: 'block'
            },
            '& figure figcaption': {
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontStyle: 'italic',
              textAlign: 'center'
            },
            '& code': {
              bgcolor: 'grey.100',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.9em',
              fontFamily: 'monospace'
            },
            '& pre': {
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 2,
              overflow: 'auto',
              mb: 3
            }
          }}
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />

        {/* FAQ — shown on the page so it matches the FAQPage schema */}
        {post.faq?.length > 0 && (
          <Box component="section" sx={{ mt: 6 }}>
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 600, mb: 3 }}
            >
              Frequently Asked Questions
            </Typography>
            {post.faq.map((item) => (
              <Box key={item.question} sx={{ mb: 3 }}>
                <Typography
                  variant="h3"
                  sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 600, mb: 1 }}
                >
                  {item.question}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8, color: 'text.secondary' }}
                >
                  {item.answer}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Tags */}
        {post.tags.length > 0 && (
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Tags:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {post.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Author Bio */}
        <Card sx={{ mt: 5, bgcolor: 'grey.50' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar
                src={post.author.avatar}
                alt={post.author.name}
                sx={{ width: 64, height: 64 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  About {post.author.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  {post.author.role}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {post.author.bio}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
          <Container maxWidth="lg">
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, textAlign: 'center' }}>
              Related Articles
            </Typography>
            <Grid container spacing={3}>
              {relatedPosts.map((relatedPost) => (
                <Grid item xs={12} sm={6} md={4} key={relatedPost.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: 2,
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <Box
                        component="img"
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        sx={{
                          height: 180,
                          objectFit: 'cover'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontSize: '1rem'
                          }}
                        >
                          {relatedPost.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {relatedPost.excerpt}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Let AI Handle the Busywork
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Revfer drafts your posts, answers customer questions and replies to reviews, in your voice. You approve everything.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(signupUrl)}
          sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
        >
          Start free
        </Button>
      </Container>
    </>
  );
};

export default BlogDetailPage;